import React from "react";
import { ClockCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Popover, Space, Typography } from "antd";
import dayjs from "dayjs";

import { type ClientActivity } from "@fitness-app/data-models/entities/ClientActivity";

import { DATE_TIME_FORMAT } from "../../constants";

interface GhostStatusPopoverProps {
  isGhost: boolean | null;
  clientActivityReport: ClientActivity;
}

interface ConditionIndicatorProps {
  isMet: boolean;
  label: string;
}

interface ActivityDetailProps {
  label: string;
  timestamp?: number | null;
}

const ConditionIndicator: React.FC<ConditionIndicatorProps> = ({ isMet, label }) => {
  return (
    <Typography.Text>
      <span style={{ color: isMet ? "#ff4d4f" : "#52c41a" }}>{isMet ? "✗" : "✓"} </span>
      <strong>{label}</strong>
    </Typography.Text>
  );
};

const ActivityDetail: React.FC<ActivityDetailProps> = ({ label, timestamp }) => {
  return (
    <Space style={{ paddingLeft: 16 }}>
      <span>{label}:</span>
      {timestamp ? (
        <>
          <ClockCircleOutlined />
          <Typography.Text strong>{dayjs.unix(timestamp).format(DATE_TIME_FORMAT)}</Typography.Text>
        </>
      ) : (
        <Typography.Text strong>Brak</Typography.Text>
      )}
    </Space>
  );
};

const GhostStatusPopover: React.FC<GhostStatusPopoverProps> = ({ isGhost, clientActivityReport }) => {
  const totalWorkouts = clientActivityReport.activities
    ? clientActivityReport.activities.reduce((total, week) => total + (week?.workoutsCompleted || 0), 0)
    : 0;

  // Calculate conditions
  const noMessageCondition =
    !clientActivityReport.lastMessageAt || dayjs().diff(dayjs.unix(clientActivityReport.lastMessageAt), "day") >= 28;

  const noLoginCondition =
    !clientActivityReport.lastLoggedAt || dayjs().diff(dayjs.unix(clientActivityReport.lastLoggedAt), "day") >= 20;

  const minimalWorkoutCondition = totalWorkouts <= 1;

  const noConsultationAndSurveyCondition =
    (!clientActivityReport.lastConsultationAt ||
      dayjs().diff(dayjs.unix(clientActivityReport.lastConsultationAt), "day") >= 30) &&
    (!clientActivityReport.lastSurveyAnsweredAt ||
      dayjs().diff(dayjs.unix(clientActivityReport.lastSurveyAnsweredAt), "day") >= 30);

  return (
    <Popover
      content={
        <Space direction="vertical" size="small" style={{ maxWidth: 400 }}>
          <Typography.Text strong>Warunki statusu ducha (spełnione co najmniej 2 z 4):</Typography.Text>

          <Space direction="vertical" size={4}>
            <ConditionIndicator isMet={noMessageCondition} label="Brak wiadomości przez 28 dni" />
            <ActivityDetail label="Ostatnia wiadomość" timestamp={clientActivityReport.lastMessageAt} />

            <ConditionIndicator isMet={noLoginCondition} label="Brak logowania przez 20 dni" />
            <ActivityDetail label="Ostatnie logowanie" timestamp={clientActivityReport.lastLoggedAt} />

            <ConditionIndicator
              isMet={minimalWorkoutCondition}
              label="Maksymalnie 1 trening wykonany (ostatnie 4 tyg.)"
            />
            <Space style={{ paddingLeft: 16 }}>
              <span>Treningów wykonanych:</span>
              <Typography.Text strong>{totalWorkouts}</Typography.Text>
            </Space>

            <ConditionIndicator
              isMet={noConsultationAndSurveyCondition}
              label="Brak konsultacji (30 dni) ORAZ brak ankiety (30 dni)"
            />
            <Space style={{ paddingLeft: 16 }} direction="vertical" size={2}>
              <ActivityDetail label="Ostatnia konsultacja" timestamp={clientActivityReport.lastConsultationAt} />
              <ActivityDetail label="Ostatnia ankieta" timestamp={clientActivityReport.lastSurveyAnsweredAt} />
            </Space>
          </Space>
        </Space>
      }
      title={isGhost ? "Dlaczego klient jest duchem?" : "Dlaczego klient nie jest duchem?"}
      trigger="hover"
    >
      <span className="flex cursor-pointer items-center gap-1 text-blue-500 underline">
        {isGhost ? "Dlaczego klient jest duchem?" : "Dlaczego klient nie jest duchem?"}
        {isGhost && <InfoCircleOutlined />}
      </span>
    </Popover>
  );
};

export default GhostStatusPopover;
